import { reportStripeRedirectRefused } from "../../../../../shared/errorReporting";
import { stripeRedirectTarget } from "../../../../../shared/lib/stripeRedirect";

/**
 * LEAVING FOR STRIPE-HOSTED CHECKOUT (payments v1.1.0, design §2.6). A
 * `checkoutUrl` is followed only when it is fresh (a `createBooking` or
 * `startCheckoutByAccessToken` answer, or a `checkoutStatus` read, never
 * storage) and passes the shared guard with Book's own host set.
 *
 * The URL is a bearer link to a payment page until it expires: it is never
 * rendered as an anchor `href`, stored, logged or reported.
 */

/**
 * THE ONLY HOST BOOK LEAVES FOR. `checkout.stripe.com` is the host of every
 * hosted Checkout Session URL Stripe documents (test and live mode alike), and
 * Backend's plan passes `session.url` through verbatim. App's Connect host is
 * deliberately NOT here: each surface leaves only for its own purpose. A
 * Stripe custom Checkout domain would be added only with Backend's evidence.
 */
export const CHECKOUT_REDIRECT_HOSTS: ReadonlySet<string> = new Set(["checkout.stripe.com"]);

/** The validated navigation target, or `null` (shared/lib/stripeRedirect.ts). */
export function checkoutRedirectTarget(raw: unknown): string | null {
  return stripeRedirectTarget(raw, CHECKOUT_REDIRECT_HOSTS);
}

/**
 * Leave for Checkout. `beforeLeave` runs only after the guard accepts, and
 * just before the navigation: it is where the "checkout in flight" marker is
 * written (design §2.1), so a refused link writes nothing.
 *
 * Returns `false`, WITHOUT navigating or calling `beforeLeave`, when the link
 * is refused; the caller shows its typed failure. A refusal reports the one
 * fixed, data-free `stripe_redirect_refused` (at most once per page).
 */
export function leaveForCheckout(raw: unknown, beforeLeave: () => boolean | void = () => {}): boolean {
  const target = checkoutRedirectTarget(raw);
  if (target === null) {
    reportStripeRedirectRefused();
    return false;
  }
  // `false` vetoes the leave (W4 F2: the identity is no longer the starter).
  if (beforeLeave() === false) return false;
  window.location.assign(target);
  return true;
}
